import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { PortalShell } from "../components/PortalShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ApiError } from "../lib/api";
import {
  changePassword,
  updateProfile,
  updateTenant,
} from "../lib/account";
import { useAuthStore } from "../stores/auth";

/**
 * Settings page (T2.9 minimum). Three sections:
 *   - Profile  : name
 *   - Workspace: tenant name (admin only)
 *   - Security : current + new password
 *
 * Email change, 2FA setup, sessions list, danger zone (account deletion)
 * land in T2.9.1+.
 */
export function SettingsPage(): JSX.Element {
  return (
    <PortalShell>
      <header className="mb-6 space-y-1">
        <h1 className="text-2xl font-semibold text-neutral-900">Settings</h1>
        <p className="text-sm text-neutral-600">
          Manage your profile, workspace, and password.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ProfileSection />
        <WorkspaceSection />
        <SecuritySection />
      </div>
    </PortalShell>
  );
}

function ProfileSection(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const setSession = useAuthStore((s) => s.setSession);
  const token = useAuthStore((s) => s.token);
  const [name, setName] = useState(user?.name ?? "");
  const [feedback, setFeedback] = useState<{ kind: "success" | "error"; text: string } | null>(null);

  const mutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: (updated) => {
      if (token !== null) {
        setSession({ token, user: updated });
      }
      setFeedback({ kind: "success", text: "Profile updated." });
    },
    onError: (err) => {
      setFeedback({
        kind: "error",
        text: err instanceof ApiError ? err.problem.detail || err.problem.title : "Update failed.",
      });
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFeedback(null);
    mutation.mutate({ name });
  }

  return (
    <SectionCard title="Profile" description="Personal details shown to teammates.">
      <form onSubmit={onSubmit} className="space-y-4">
        <Input
          label="Email"
          value={user?.email ?? ""}
          readOnly
          hint="Email change lands in T2.9.1."
        />
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={255}
        />
        <Feedback feedback={feedback} />
        <Button type="submit" variant="primary" loading={mutation.isPending}>
          Save profile
        </Button>
      </form>
    </SectionCard>
  );
}

function WorkspaceSection(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === "admin";
  const [name, setName] = useState("");
  const [feedback, setFeedback] = useState<{ kind: "success" | "error"; text: string } | null>(null);

  const mutation = useMutation({
    mutationFn: updateTenant,
    onSuccess: (tenant) => {
      setName(tenant.name);
      setFeedback({ kind: "success", text: "Workspace updated." });
    },
    onError: (err) => {
      setFeedback({
        kind: "error",
        text: err instanceof ApiError ? err.problem.detail || err.problem.title : "Update failed.",
      });
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFeedback(null);
    mutation.mutate({ name });
  }

  return (
    <SectionCard
      title="Workspace"
      description={
        isAdmin
          ? "Change the workspace name."
          : "Only admins can change workspace settings."
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Input
          label="Workspace name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={!isAdmin}
          placeholder="Acme Corp"
        />
        <Feedback feedback={feedback} />
        <Button
          type="submit"
          variant="primary"
          disabled={!isAdmin}
          loading={mutation.isPending}
        >
          Save workspace
        </Button>
      </form>
    </SectionCard>
  );
}

function SecuritySection(): JSX.Element {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [feedback, setFeedback] = useState<{ kind: "success" | "error"; text: string } | null>(null);

  const mutation = useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      setCurrentPassword("");
      setNewPassword("");
      setFeedback({ kind: "success", text: "Password changed. Other sessions were signed out." });
    },
    onError: (err) => {
      setFeedback({
        kind: "error",
        text: err instanceof ApiError ? err.problem.detail || err.problem.title : "Couldn't change password.",
      });
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFeedback(null);
    mutation.mutate({
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  return (
    <SectionCard
      title="Security"
      description="Change your password. Other devices will be signed out."
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Input
          label="Current password"
          type="password"
          autoComplete="current-password"
          required
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          required
          minLength={12}
          hint="Min 12 characters."
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <Feedback feedback={feedback} />
        <Button type="submit" variant="primary" loading={mutation.isPending}>
          Change password
        </Button>
      </form>
    </SectionCard>
  );
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
      <header className="mb-4 space-y-1">
        <h2 className="text-lg font-semibold text-neutral-900">{title}</h2>
        <p className="text-sm text-neutral-600">{description}</p>
      </header>
      {children}
    </section>
  );
}

function Feedback({
  feedback,
}: {
  feedback: { kind: "success" | "error"; text: string } | null;
}): JSX.Element | null {
  if (feedback === null) return null;
  const kindClasses =
    feedback.kind === "success"
      ? "border-success-200 bg-success-50 text-success-700"
      : "border-error-200 bg-error-50 text-error-700";
  return (
    <p
      role={feedback.kind === "error" ? "alert" : "status"}
      className={`rounded-md border px-3 py-2 text-sm ${kindClasses}`}
    >
      {feedback.text}
    </p>
  );
}
